import type { FrameworkClass } from './framework.d';
import { FrameworkClasses } from './frameworkClasses';

/**
 * Single source of truth for the set of action names that count as
 * "axis input control" methods on framework classes.
 *
 * Collected from every `FrameworkClass.axisActions[].name` so adding a new
 * axis action in `frameworkClasses.ts` automatically flows through to all
 * code-generation and code-scanning paths (see `controllerCodeUtils.ts` and
 * `hardwareParser.ts`).
 */

let cachedNames: Set<string> | null = null;
let cachedAlternation: string | null = null;
let cachedInputArgByAction: Map<string, string> | null = null;
let cachedInputArgAlternation: string | null = null;

function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Every distinct `axisActions[].name` declared in `FrameworkClasses`. */
export function getFrameworkAxisActionNames(): Set<string> {
  if (cachedNames) return cachedNames;
  const names = new Set<string>();
  for (const cls of FrameworkClasses as FrameworkClass[]) {
    for (const action of cls.axisActions || []) {
      const name = String(action?.name || '').trim();
      if (name) names.add(name);
    }
  }
  cachedNames = names;
  return names;
}

/**
 * Maps every axis action name to the name of its "input callable" argument
 * (the first argument in `arguments`, which is the one bound to a controller
 * axis getter such as `controller.get_l1_axis`).
 *
 * Falls back silently for axis actions with no arguments declared (entry is
 * omitted from the map).
 */
export function getFrameworkAxisActionInputArgNames(): Map<string, string> {
  if (cachedInputArgByAction) return cachedInputArgByAction;
  const map = new Map<string, string>();
  for (const cls of FrameworkClasses as FrameworkClass[]) {
    for (const action of cls.axisActions || []) {
      const name = String(action?.name || '').trim();
      const firstArg = String(action?.arguments?.[0]?.name || '').trim();
      if (name && firstArg && !map.has(name)) map.set(name, firstArg);
    }
  }
  cachedInputArgByAction = map;
  return map;
}

/** Looks up the input-callable argument name for a given axis action. */
export function getInputArgNameForAxisAction(actionName: string): string | undefined {
  return getFrameworkAxisActionInputArgNames().get(actionName);
}

/**
 * Non-capturing regex alternation of every distinct "input argument" name
 * across all framework axis actions, e.g. `(?:variable_input)` (or
 * `(?:variable_input|axis_input)` if a future action uses a different name).
 *
 * Returns `(?!)` when no axis actions are declared.
 */
export function getFrameworkAxisInputArgAlternation(): string {
  if (cachedInputArgAlternation !== null) return cachedInputArgAlternation;
  const names = new Set<string>(getFrameworkAxisActionInputArgNames().values());
  if (names.size === 0) {
    cachedInputArgAlternation = '(?!)';
    return cachedInputArgAlternation;
  }
  cachedInputArgAlternation = `(?:${Array.from(names).map(escapeRegex).join('|')})`;
  return cachedInputArgAlternation;
}

/**
 * Non-capturing regex alternation of every axis-action name, suitable for
 * embedding into a larger pattern,
 * e.g. `\\.(add_variable_velocity_control|add_variable_percent_control|...)\\(`.
 *
 * If no axis actions are declared the helper returns `(?!)` so the surrounding
 * regex never matches (rather than producing a malformed alternation).
 */
export function getFrameworkAxisActionAlternation(): string {
  if (cachedAlternation !== null) return cachedAlternation;
  const names = Array.from(getFrameworkAxisActionNames());
  if (names.length === 0) {
    cachedAlternation = '(?!)';
    return cachedAlternation;
  }
  cachedAlternation = `(?:${names.map(escapeRegex).join('|')})`;
  return cachedAlternation;
}

/**
 * Body of a capturing group containing the regex alternation of every
 * axis-action name (without the surrounding parentheses), for callers that
 * want to capture which axis action was matched.
 *
 * Returns `(?!)` when no axis actions are declared, matching nothing.
 */
export function getFrameworkAxisActionCaptureBody(): string {
  const names = Array.from(getFrameworkAxisActionNames());
  if (names.length === 0) return '(?!)';
  return names.map(escapeRegex).join('|');
}

/** Test-only: reset cached state. */
export function _resetFrameworkAxisActionCacheForTests(): void {
  cachedNames = null;
  cachedAlternation = null;
  cachedInputArgByAction = null;
  cachedInputArgAlternation = null;
}
